import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Plus, Pencil, Trash2, Copy, Award, Loader2 } from 'lucide-react'
import type { TemplateConfig } from './templatePresets'
import CertificatePreview from './CertificatePreview'

interface CertificateTemplate {
  id: string
  name: string
  description: string
  design_config: TemplateConfig
  background_image_url: string | null
  is_default: boolean
  created_at: string
}

export default function CertificateTemplates() {
  const [templates, setTemplates] = useState<CertificateTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    const { data } = await supabase
      .from('certificate_templates')
      .select('*')
      .order('created_at', { ascending: false })

    if (data) setTemplates(data as CertificateTemplate[])
    setLoading(false)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta plantilla?')) return
    setDeleting(id)
    await supabase.from('certificate_templates').delete().eq('id', id)
    setTemplates(templates.filter((t) => t.id !== id))
    setDeleting(null)
  }

  const handleDuplicate = async (template: CertificateTemplate) => {
    const { data } = await (supabase.from('certificate_templates') as any).insert({
      name: `${template.name} (copia)`,
      description: template.description,
      design_config: template.design_config,
      background_image_url: template.background_image_url,
      is_default: false,
    }).select().single()

    if (data) setTemplates([data, ...templates])
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plantillas de Certificados</h1>
          <p className="text-gray-600">Crea y gestiona plantillas para certificados de alumnos</p>
        </div>
        <Link
          to="/admin/certificates/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nueva plantilla
        </Link>
      </div>

      {templates.length === 0 ? (
        <div className="bg-white rounded-xl border shadow-sm p-12 text-center">
          <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay plantillas</h3>
          <p className="text-gray-500 mb-6">Crea tu primera plantilla de certificado. Puedes partir de uno de los 10 modelos predefinidos.</p>
          <Link
            to="/admin/certificates/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080]"
          >
            <Plus className="w-5 h-5" />
            Crear plantilla
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-xl border shadow-sm overflow-hidden group">
              {/* Preview */}
              <div className="aspect-[800/566] bg-gray-50 overflow-hidden border-b">
                <div className="w-full h-full flex items-center justify-center p-2">
                  <div className="w-full">
                    <CertificatePreview
                      config={template.design_config}
                      backgroundImageUrl={template.background_image_url}
                    />
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    {template.description && (
                      <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                    )}
                  </div>
                  {template.is_default && (
                    <span className="px-2 py-0.5 bg-[#0055A4]/10 text-[#0055A4] text-xs font-medium rounded">
                      Default
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <Link
                    to={`/admin/certificates/${template.id}/edit`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Editar
                  </Link>
                  <button
                    onClick={() => handleDuplicate(template)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Duplicar
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    disabled={deleting === template.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 rounded-lg text-red-600 transition-colors ml-auto disabled:opacity-50"
                  >
                    {deleting === template.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
